/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useRef, useState } from 'react';
import { Button } from './button';
import { Paperclip, X } from 'lucide-react';
import { cn } from '@shared/lib/utils';

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  accept?: string;
  maxSize?: number; // in bytes
  className?: string;
  disabled?: boolean;
}

const SUPPORTED_FILE_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'text/csv',
  'application/json',
  'application/xml',
  'text/xml'
];

const DEFAULT_ACCEPT = '.pdf,.txt,.md,.csv,.json,.xml';
const DEFAULT_MAX_SIZE = 50 * 1024 * 1024; // 50MB

export function FileUpload({
  onFileSelect,
  selectedFile,
  accept = DEFAULT_ACCEPT,
  maxSize = DEFAULT_MAX_SIZE,
  className,
  disabled = false
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Unsupported file type: ${file.type}. Supported types: PDF, TXT, MD, CSV, JSON, XML`
      };
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      const fileSizeMB = Math.round(file.size / (1024 * 1024));
      return {
        valid: false,
        error: `File too large: ${fileSizeMB}MB. Maximum size is ${maxSizeMB}MB.`
      };
    }

    return { valid: true };
  };

  const handleFileSelect = (file: File) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      // You might want to show a toast error here
      console.error('File validation failed:', validation.error);
      return;
    }
    onFileSelect(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleButtonClick = () => {
    if (selectedFile) {
      onFileSelect(null); // Remove file if one is selected
    } else {
      fileInputRef.current?.click(); // Open file picker
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={cn('relative', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />
      
      {selectedFile ? (
        <FileChip
          file={selectedFile}
          onRemove={() => onFileSelect(null)}
          disabled={disabled}
        />
      ) : (
        <div
          className={cn(
            'transition-colors',
            dragOver && 'bg-blue-50 dark:bg-blue-950'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleButtonClick}
            disabled={disabled}
            className={cn(
              'h-8 w-8',
              dragOver && 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950'
            )}
            title="Attach file"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

interface FileChipProps {
  file: File;
  onRemove: () => void;
  disabled?: boolean;
}

export function FileChip({ file, onRemove, disabled = false }: FileChipProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string): string => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('text')) return '📝';
    if (type.includes('json')) return '📋';
    if (type.includes('xml')) return '📋';
    if (type.includes('csv')) return '📊';
    return '📎';
  };

  return (
    <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md px-2 py-1 text-sm">
      <span className="text-base">{getFileIcon(file.type)}</span>
      <div className="flex flex-col">
        <span className="font-medium text-blue-900 dark:text-blue-100 truncate max-w-[150px]" title={file.name}>
          {file.name}
        </span>
        <span className="text-xs text-blue-600 dark:text-blue-400">
          {formatFileSize(file.size)}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={disabled}
        className="h-4 w-4 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
        title="Remove file"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

export function isFileAttachmentSupported(file: File): boolean {
  return SUPPORTED_FILE_TYPES.includes(file.type);
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}